import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12 bg-[#FFFFFF]">
      <Card className="w-full max-w-md card-soft border-0">
        <CardContent className="p-7 sm:p-10">
          <button
            onClick={() => navigate("/login")}
            data-testid="forgot-back-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#6b7f79] hover:text-[#2D5951] mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Retour à la connexion
          </button>

          <div className="flex items-center gap-3 mb-7">
            <img
              src="/logo-cantine.png"
              alt="La Cantine de Géraldine"
              className="h-14 w-auto object-contain"
              data-testid="forgot-logo"
            />
          </div>

          {done ? (
            <div data-testid="forgot-confirmation">
              <h2 className="font-display text-2xl text-[#1A2B27] mb-2 flex items-center gap-2">
                <Mail className="w-6 h-6 text-[#2D5951]" /> C'est envoyé
              </h2>
              <p className="text-sm text-[#6b7f79] leading-relaxed">
                Si <b>{email}</b> correspond à un compte, un email avec un lien de réinitialisation vient d'être envoyé.
                Le lien est valable <b>1 heure</b>.
              </p>
              <p className="text-xs text-[#94ADA7] mt-4">
                Pense à vérifier tes spams si tu ne le vois pas dans la minute.
              </p>
              <Link to="/login">
                <Button
                  className="mt-7 w-full h-12 rounded-full bg-[#2D5951] hover:bg-[#1F423C] text-white font-semibold"
                  data-testid="forgot-back-to-login"
                >
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-display text-2xl text-[#1A2B27] mb-1">Mot de passe oublié ?</h2>
              <p className="text-sm text-[#6b7f79] mb-6">
                Indique ton email — nous t'enverrons un lien pour en choisir un nouveau.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="label-tiny">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="prenom@lacantinedegeraldine.be"
                    data-testid="forgot-email-input"
                    className="mt-2 h-12 rounded-xl border-[#DDE7E4] focus-visible:ring-[#2D5951]"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  data-testid="forgot-submit-button"
                  className="w-full h-12 rounded-full bg-[#2D5951] hover:bg-[#1F423C] text-white font-semibold tracking-wide transition-all duration-200 hover:-translate-y-0.5 shadow-md"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le lien"}
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
